/**
 * Data patches (docs/DATA_PIPELINE.md "Patches"): hand-authored, asserted edits to exported data.
 *
 * Why runtime: public/data/*.json is regenerated byte-for-byte from CardKnight3D by tools/export_data.py
 * and CardKnight3D is read-only. A fix to an editor marker therefore lives in public/data/patches.json
 * and is applied here, right after map_overrides.json loads. Every patch asserts the value it replaces
 * (`old`), so if the exporter output changes under a patch the load fails loudly instead of silently
 * writing over new data.
 *
 * Patch shape (patches.json `patches[]`):
 *   id      unique name          map     key in map_overrides.json
 *   target  { list: 'fx'|'exits'|'npcs'|'props'|..., where: {field: value, ...} }
 *           `where` must match exactly ONE entry. For a dict container (npcs keyed by id/name)
 *           use where: { key: '<name>' }.
 *   field   dotted path inside the entry ('x', 'tile.0')
 *   old/new asserted previous value / replacement      reason  why (shown in failures and docs)
 *   flag    optional: only applied while patches.json `flags[flag]` is true
 *   file    optional: 'map_overrides' (default) or 'map_defs' (map_defs_raw.json `maps`; the authored
 *           map_defs side the overrides merge onto: interactables, gated objects). RULING 2026-10-09 (arcana_sw)
 *   op      optional: 'set' (default) or 'add'. `add` asserts ABSENCE instead of `old`: for an array
 *           container `where` must match NO entry and `new` is appended; for a dict container the key
 *           must be missing and `new` becomes its value (`field` unused). RULING 2026-10-09 (arcana_sw)
 *
 * `flags` also carries behaviour switches read by game code through `flag(name)` (no data edit):
 * `deepvein_editor_hazards` makes the interior loader read map_overrides.deepvein.hazards.
 */

export interface PatchTarget { list: string; where: Record<string, unknown> }
export interface DataPatch {
  id: string;
  map: string;
  target: PatchTarget;
  field: string;
  old: unknown;
  new: unknown;
  reason: string;
  flag?: string;
  file?: 'map_overrides' | 'map_defs';
  op?: 'set' | 'add';
}
export interface PatchesFile {
  version: number;
  flags: Record<string, boolean>;
  /** Map titles the exported profile_catalogs.json `mapNames` lacks (applyMapNames). */
  mapNames?: Record<string, string>;
  patches: DataPatch[];
}

let flags: Record<string, boolean> = {};

/**
 * Fill MAP_NAMES (profile_catalogs.json `mapNames`, read by profile.map_name for the pause footer and
 * save slots) from patches.json `mapNames`. The Python table only named 10 maps, so every other room
 * fell back to its title-cased id ("Home4"). Fill only: a name the exporter already ships wins, so a
 * later Python label is never overwritten. Returns the ids it filled.
 */
export function applyMapNames(names: Record<string, string>, file: Pick<PatchesFile, 'mapNames'> | undefined): string[] {
  const filled: string[] = [];
  for (const [id, name] of Object.entries(file?.mapNames ?? {})) {
    if (Object.prototype.hasOwnProperty.call(names, id)) continue;
    names[id] = name;
    filled.push(id);
  }
  return filled;
}

/** Behaviour switch from patches.json (false until a patches file has been installed). */
export function flag(name: string): boolean {
  return flags[name] === true;
}

/** Install the flags of a loaded patches file (and let tests override them). */
export function setFlags(f: Record<string, boolean>): void {
  flags = { ...f };
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
/** `where` keys may be dotted paths ('stages.0.set_flag') to tell apart id-less entries on one tile.
 *  RULING 2026-10-09 (arcana_sw) */
const getPath = (e: any, k: string): unknown => k.split('.').reduce((o: any, part) => (o == null ? undefined : o[part]), e);

function resolveEntry(ovr: Record<string, any>, p: DataPatch): any {
  const m = ovr[p.map];
  if (!m) throw new Error(`patch ${p.id}: map ${p.map} not in map_overrides`);
  const cont = m[p.target.list];
  if (cont === undefined) throw new Error(`patch ${p.id}: ${p.map}.${p.target.list} missing`);
  const where = p.target.where;
  if (Array.isArray(cont)) {
    const hits = cont.filter((e: any) => Object.entries(where).every(([k, v]) => same(getPath(e, k), v)));
    if (hits.length !== 1) {
      throw new Error(`patch ${p.id}: target ${JSON.stringify(where)} matched ${hits.length} entries in ${p.map}.${p.target.list} (need exactly 1)`);
    }
    return hits[0];
  }
  const key = String((where as any).key);
  if (!(key in cont)) throw new Error(`patch ${p.id}: ${p.map}.${p.target.list} has no key ${key}`);
  return cont[key];
}

/** op 'add': append to an array container / create a dict key, asserting it was absent. */
function addEntry(ovr: Record<string, any>, p: DataPatch): void {
  const m = ovr[p.map];
  if (!m) throw new Error(`patch ${p.id}: map ${p.map} not in ${p.file ?? 'map_overrides'}`);
  const cont = m[p.target.list];
  if (cont === undefined) throw new Error(`patch ${p.id}: ${p.map}.${p.target.list} missing`);
  if (Array.isArray(cont)) {
    const hits = cont.filter((e: any) => Object.entries(p.target.where).every(([k, v]) => same(getPath(e, k), v)));
    if (hits.length) throw new Error(`patch ${p.id} is stale: add target ${JSON.stringify(p.target.where)} already present in ${p.map}.${p.target.list} (${p.reason})`);
    cont.push(p.new);
    return;
  }
  const key = String((p.target.where as any).key);
  if (key in cont) throw new Error(`patch ${p.id} is stale: ${p.map}.${p.target.list} already has key ${key} (${p.reason})`);
  cont[key] = p.new;
}

function walk(entry: any, field: string, id: string): [any, string] {
  const parts = field.split('.');
  let o = entry;
  for (const part of parts.slice(0, -1)) {
    o = o?.[part];
    if (o === undefined || o === null) throw new Error(`patch ${id}: field path ${field} breaks at ${part}`);
  }
  return [o, parts[parts.length - 1]];
}

const done = new WeakSet<object>();

/**
 * Apply every enabled patch to a parsed map_overrides.json, in place. Throws (naming the patch) when a
 * target is missing/ambiguous or the current value is not `old`. Idempotent per object.
 * Returns the ids applied.
 */
export function applyPatches(ovr: Record<string, any>, file: PatchesFile, flagOverride?: Record<string, boolean>,
                             target: 'map_overrides' | 'map_defs' = 'map_overrides'): string[] {
  if (done.has(ovr)) return [];
  const fl = { ...file.flags, ...(flagOverride ?? {}) };
  const applied: string[] = [];
  const ids = new Set<string>();
  for (const p of file.patches) {
    if (ids.has(p.id)) throw new Error(`patch ${p.id}: duplicate id`);
    ids.add(p.id);
    if (p.flag && fl[p.flag] !== true) continue;
    if ((p.file ?? 'map_overrides') !== target) continue;
    if (p.op === 'add') { addEntry(ovr, p); applied.push(p.id); continue; }   // RULING 2026-10-09 (arcana_sw)
    const entry = resolveEntry(ovr, p);
    const [o, k] = walk(entry, p.field, p.id);
    // old: null also asserts an ABSENT field (adds a field to an existing entry). RULING 2026-10-09 (arcana_sw)
    if (!same(o[k], p.old) && !(p.old === null && o[k] === undefined)) {
      throw new Error(`patch ${p.id} is stale: ${p.map}.${p.target.list} ${JSON.stringify(p.target.where)} .${p.field} is ${JSON.stringify(o[k])}, expected ${JSON.stringify(p.old)} (${p.reason})`);
    }
    o[k] = p.new;
    applied.push(p.id);
  }
  done.add(ovr);
  return applied;
}
